import { cardOf, defIdOf, getStages } from './data/registry';
import { paymentContextFor } from './payment/context';
import type { PaymentBlock, PaymentOption } from './payment/solver';
import { computePaymentOptions, validatePayment } from './payment/solver';
import type { Perspective, PublicCity } from './perspective';
import { hasEffect } from './production';
import type { CardDef, CardIid } from './types/cards';
import type { FreeReason, Selection } from './types/state';
import type { EngineErrorCode, Result } from './errors';
import { fail, ok } from './errors';

export type BuildBlock = 'DUPLICATE_BUILDING' | PaymentBlock;
export type WonderBlock = 'WONDER_COMPLETE' | 'NO_SIDE' | PaymentBlock;

export interface CardActions {
  card: CardIid;
  build:
    | { ok: true; free: FreeReason | null; options: PaymentOption[] }
    | { ok: false; reason: BuildBlock };
  wonder: { ok: true; stage: number; options: PaymentOption[] } | { ok: false; reason: WonderBlock };
  discard: { ok: true; coins: number };
}

export const DISCARD_COINS = 3;

const EMPTY_OPTION: PaymentOption = { buy: { left: {}, right: {} }, coins: { bank: 0, left: 0, right: 0 }, total: 0 };

function me(p: Perspective): PublicCity {
  const c = p.cities[p.seat];
  if (!c) throw new Error(`Asiento inválido ${p.seat}`);
  return c;
}

export function ownsDef(c: PublicCity, defId: string): boolean {
  return c.city.some((iid) => defIdOf(iid) === defId);
}

/** ¿Se puede construir gratis? (encadenamiento o efectos de Olimpia). Nunca aplica a etapas. */
export function freeReasonFor(p: Perspective, def: CardDef): FreeReason | null {
  const c = me(p);
  if (def.chainFrom?.some((id) => ownsDef(c, id))) return 'chain';
  if (hasEffect(c, 'freeFirstOfColor') && !c.city.some((iid) => cardOf(iid).color === def.color)) {
    return 'freeFirstOfColor';
  }
  if (p.mode === 'turn' && p.turn === 1 && hasEffect(c, 'freeFirstOfAge')) return 'freeFirstOfAge';
  if (p.mode === 'turn' && p.turn === 6 && hasEffect(c, 'freeLastOfAge')) return 'freeLastOfAge';
  return null;
}

export function buildActions(p: Perspective, iid: CardIid): CardActions['build'] {
  const def = cardOf(iid);
  const c = me(p);
  if (ownsDef(c, def.id)) return { ok: false, reason: 'DUPLICATE_BUILDING' };
  const free = freeReasonFor(p, def);
  if (free) return { ok: true, free, options: [EMPTY_OPTION] };
  const res = computePaymentOptions(paymentContextFor(p), def.cost);
  return res.ok ? { ok: true, free: null, options: res.options } : { ok: false, reason: res.reason };
}

export function wonderActions(p: Perspective): CardActions['wonder'] {
  const c = me(p);
  if (!c.side) return { ok: false, reason: 'NO_SIDE' };
  const stages = getStages(c.wonder, c.side);
  const stage = stages[c.stagesBuilt];
  if (!stage) return { ok: false, reason: 'WONDER_COMPLETE' };
  const res = computePaymentOptions(paymentContextFor(p), stage.cost);
  return res.ok ? { ok: true, stage: c.stagesBuilt, options: res.options } : { ok: false, reason: res.reason };
}

/** Jugadas legales para cada carta de la mano (mismo código en cliente y servidor). */
export function getLegalActions(p: Perspective): CardActions[] {
  const wonder = wonderActions(p);
  return p.hand.map((card) => ({
    card,
    build: buildActions(p, card),
    wonder,
    discard: { ok: true, coins: DISCARD_COINS },
  }));
}

export interface ResolvedSelection {
  selection: Selection;
  paid: { bank: number; left: number; right: number };
  free: FreeReason | null;
  stage?: number;
}

/** Valida una selección contra la perspectiva y calcula exactamente qué se paga. */
export function resolveSelection(p: Perspective, selection: Selection): Result<ResolvedSelection> {
  if (!p.hand.includes(selection.card)) return fail('CARD_NOT_IN_HAND');
  const c = me(p);
  const zero = { bank: 0, left: 0, right: 0 };
  switch (selection.action) {
    case 'discard':
      return ok({ selection: { action: 'discard', card: selection.card }, paid: zero, free: null });
    case 'build': {
      const def = cardOf(selection.card);
      if (ownsDef(c, def.id)) return fail('DUPLICATE_BUILDING');
      const free = freeReasonFor(p, def);
      if (free) {
        return ok({
          selection: { action: 'build', card: selection.card, payment: { buy: { left: {}, right: {} } } },
          paid: zero,
          free,
        });
      }
      const v = validatePayment(paymentContextFor(p), def.cost, selection.payment?.buy ?? { left: {}, right: {} });
      if (!v.ok) return fail(v.reason as EngineErrorCode);
      return ok({ selection: normalizeSelection(selection), paid: v.coins, free: null });
    }
    case 'wonder': {
      if (!c.side) return fail('INVALID_COMMAND', 'Lado de maravilla sin elegir');
      const stage = getStages(c.wonder, c.side)[c.stagesBuilt];
      if (!stage) return fail('WONDER_COMPLETE');
      const v = validatePayment(paymentContextFor(p), stage.cost, selection.payment?.buy ?? { left: {}, right: {} });
      if (!v.ok) return fail(v.reason as EngineErrorCode);
      return ok({ selection: normalizeSelection(selection), paid: v.coins, free: null, stage: c.stagesBuilt });
    }
    default:
      return fail('INVALID_COMMAND');
  }
}

function normalizeSelection(s: Selection): Selection {
  if (s.action === 'discard') return { action: 'discard', card: s.card };
  const clean = (rc: Record<string, number | undefined>) =>
    Object.fromEntries(Object.entries(rc).filter(([, v]) => typeof v === 'number' && v > 0));
  return {
    action: s.action,
    card: s.card,
    payment: { buy: { left: clean(s.payment?.buy?.left ?? {}), right: clean(s.payment?.buy?.right ?? {}) } },
  };
}
